import { useCallback, useState } from "react";
import { useBulkMarkTransactionsForDeletion } from "@api/transactionDeletion.api";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { CounterpartyCreationResponse } from "@common/contracts/counterparty.contract";
import { pluralize } from "@common/helpers/string";
import { useBulkActions } from "./useBulkActions";

interface UseTransactionBulkModalsParams {
    // Restricts the post-create counterparty assignment to this subset
    // (excludes transactions matched to an invoice or bill, where the
    // counterparty is locked server-side) — mirrors the existing-counterparty
    // selection path in useTransactionCommandOptions.
    unlockedTransactionIds?: number[];
}

export function useTransactionBulkModals({
    unlockedTransactionIds,
}: UseTransactionBulkModalsParams = {}) {
    const bulkActions = useBulkActions();
    const bulkDeleteMutation = useBulkMarkTransactionsForDeletion();
    const { error: toastError } = useToaster();

    const [shouldShowUnmatch, setShouldShowUnmatch] = useState(false);
    const [shouldShowUnsplit, setShouldShowUnsplit] = useState(false);
    const [shouldShowRevertGL, setShouldShowRevertGL] = useState(false);
    const [shouldShowRemoveCounterparty, setShouldShowRemoveCounterparty] =
        useState(false);
    const [shouldShowRemoveCustomer, setShouldShowRemoveCustomer] =
        useState(false);
    const [shouldShowDelete, setShouldShowDelete] = useState(false);
    const [shouldShowGusto, setShouldShowGusto] = useState(false);
    const [shouldShowMemo, setShouldShowMemo] = useState(false);

    const confirmRevertGL = useCallback(async () => {
        try {
            await bulkActions.updateSelected(
                { update: "coa", value: null },
                unlockedTransactionIds
                    ? { targetTransactionIds: unlockedTransactionIds }
                    : undefined,
            );
            setShouldShowRevertGL(false);
        } catch {
            toastError("Failed to revert cash ledger");
        }
    }, [bulkActions, toastError, unlockedTransactionIds]);

    const confirmRemoveCounterparty = useCallback(async () => {
        try {
            await bulkActions.updateSelected(
                {
                    update: "counterpartyId",
                    value: null,
                },
                unlockedTransactionIds
                    ? { targetTransactionIds: unlockedTransactionIds }
                    : undefined,
            );
            setShouldShowRemoveCounterparty(false);
        } catch {
            toastError("Failed to remove counterparty");
        }
    }, [bulkActions, toastError, unlockedTransactionIds]);

    const confirmRemoveCustomer = useCallback(async () => {
        try {
            await bulkActions.updateSelected({
                update: "customerId",
                value: null,
            });
            setShouldShowRemoveCustomer(false);
        } catch {
            toastError("Failed to remove customer");
        }
    }, [bulkActions, toastError]);

    const confirmDelete = useCallback(async () => {
        const selectedIds = Object.values(bulkActions.selected).map(
            (t) => t.id,
        );
        try {
            const response = await bulkDeleteMutation.mutateAsync(selectedIds);
            if (response.status === 200) {
                const failedCount = response.body.results.filter(
                    (r) => !r.success,
                ).length;
                if (failedCount === selectedIds.length) {
                    toastError(
                        "None of the selected transactions could be deleted",
                    );
                } else if (failedCount > 0) {
                    toastError(
                        `${failedCount} ${pluralize("transaction", failedCount)} couldn't be deleted`,
                    );
                }
                if (failedCount < selectedIds.length) {
                    bulkActions.clearSelection();
                }
            }
        } catch {
            toastError("Failed to delete transactions");
        } finally {
            setShouldShowDelete(false);
        }
    }, [bulkActions, bulkDeleteMutation, toastError]);

    const handleCreateCounterpartyClosed = useCallback(
        async (creationResponse?: CounterpartyCreationResponse) => {
            if (creationResponse) {
                try {
                    await bulkActions.updateSelected(
                        {
                            update: "counterpartyId",
                            value: creationResponse.counterparty.id,
                            counterpartyName:
                                creationResponse.counterparty.name,
                        },
                        unlockedTransactionIds
                            ? { targetTransactionIds: unlockedTransactionIds }
                            : undefined,
                    );
                } catch {
                    toastError("Failed to assign counterparty");
                }
            }
        },
        [bulkActions, toastError, unlockedTransactionIds],
    );

    const handleCreateCustomerClosed = useCallback(
        async (creationResponse?: CounterpartyCreationResponse) => {
            if (creationResponse) {
                try {
                    await bulkActions.updateSelected({
                        update: "customerId",
                        value: creationResponse.counterparty.id,
                        customerName: creationResponse.counterparty.name,
                    });
                } catch {
                    toastError("Failed to assign customer");
                }
            }
        },
        [bulkActions, toastError],
    );

    return {
        shouldShowUnmatch,
        setShouldShowUnmatch,
        shouldShowUnsplit,
        setShouldShowUnsplit,
        shouldShowRevertGL,
        setShouldShowRevertGL,
        confirmRevertGL,
        shouldShowRemoveCounterparty,
        setShouldShowRemoveCounterparty,
        confirmRemoveCounterparty,
        shouldShowRemoveCustomer,
        setShouldShowRemoveCustomer,
        confirmRemoveCustomer,
        shouldShowDelete,
        setShouldShowDelete,
        confirmDelete,
        shouldShowGusto,
        setShouldShowGusto,
        shouldShowMemo,
        setShouldShowMemo,
        handleCreateCounterpartyClosed,
        handleCreateCustomerClosed,
    };
}
